import { useMemo } from 'react';
import { db } from '../instant';
import type { PackingListItem } from '../schema';
import { sortEventsByStartDateThenName } from '../utils/eventSort';

/** Minimal event shape for usePackingListsByEvent (id required; startDate/name/endDate for sort and filter). */
export type EventWithId = {
    id: string;
    startDate?: string | number | null;
    endDate?: string | number | null;
    name?: string | null;
};

/**
 * Query events with packing list items and derive packingListItemsByEvent + eventsWithPackingLists.
 * Used by CreateTripDialog, EditTripDialog, CreateOperationDialog.
 */
export function usePackingListsByEvent(
    events: EventWithId[],
    options?: { allowedEventIds?: string[] }
): {
    packingListItemsByEvent: Map<string, PackingListItem[]>;
    eventsWithPackingLists: EventWithId[];
} {
    const { data } = db.useQuery({
        events: {
            packingListItems: {
                itemType: {
                    category: {},
                },
            },
        },
    });

    const packingListItemsByEvent = useMemo(() => {
        const map = new Map<string, PackingListItem[]>();
        if (!data?.events) return map;
        for (const event of data.events) {
            const packingListItems = (event.packingListItems || []) as PackingListItem[];
            if (packingListItems.length > 0) {
                map.set(event.id, packingListItems);
            }
        }
        return map;
    }, [data]);

    const eventsWithPackingLists = useMemo(() => {
        let list = events.filter(e => packingListItemsByEvent.has(e.id));
        if (options?.allowedEventIds !== undefined) {
            const allowed = new Set(options.allowedEventIds);
            list = list.filter(e => allowed.has(e.id));
        }
        return sortEventsByStartDateThenName(list) as EventWithId[];
    }, [events, packingListItemsByEvent, options]);

    return { packingListItemsByEvent, eventsWithPackingLists };
}
